import React, { useState, useEffect, useRef } from "react";
import { X, Play, Pause, Volume2, RotateCcw } from "lucide-react";

export default function AudioOverviewModal({ note, onClose }) {
  const [dialogue, setDialogue] = useState([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const isPlayingRef = useRef(false);
  const synthRef = useRef(null);

  const cleanNoteText = (note?.html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  useEffect(() => {
    // Generate dual-persona academic dialogue script
    const title = note?.title || "Research Overview";
    const sentences = cleanNoteText
      .split(/[.!?]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 10);

    const script = [
      {
        speaker: "Analyst A",
        role: "Host & Synthesizer",
        text: `Welcome to this audio synthesis of "${title}". Let us break down the primary theoretical proposition established in these notes.`,
      },
      {
        speaker: "Analyst B",
        role: "Specialist",
        text: sentences[0]
          ? `The core argument begins with this premise: "${sentences[0]}". This sets up the foundational scope of the topic.`
          : `The central premise addresses the structural dynamics and fundamental mechanisms under investigation.`,
      },
      {
        speaker: "Analyst A",
        role: "Host & Synthesizer",
        text: sentences[1]
          ? `And what empirical mechanism bridges this claim to its broader implications?`
          : `How does this connect to real-world application or counter-perspectives in the literature?`,
      },
      {
        speaker: "Analyst B",
        role: "Specialist",
        text: sentences[1]
          ? `Specifically, note the observation: "${sentences[1]}". This isolates the key variables and highlights the primary evidential data.`
          : `The evidence demonstrates that under standard boundary conditions, the expected outcome holds consistently across comparative models.`,
      },
      {
        speaker: "Analyst A",
        role: "Host & Synthesizer",
        text: `To summarize for examination purposes: identify the core premise, verify the primary supporting citation, and prepare to address alternative interpretations.`,
      },
    ];

    setDialogue(script);
    synthRef.current = window.speechSynthesis;

    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, [note, cleanNoteText]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const speakLine = (index) => {
    if (!synthRef.current || index >= dialogue.length) {
      setIsPlaying(false);
      return;
    }

    synthRef.current.cancel();
    setCurrentLineIndex(index);

    const line = dialogue[index];
    const utterance = new SpeechSynthesisUtterance(line.text);
    utterance.rate = playbackSpeed;
    utterance.pitch = line.speaker === "Analyst A" ? 1.05 : 0.95;

    // Pick available voices
    const voices = synthRef.current.getVoices();
    if (voices && voices.length > 1) {
      utterance.voice = line.speaker === "Analyst A" ? voices[0] : voices[1] || voices[0];
    }

    utterance.onend = () => {
      if (isPlayingRef.current && index + 1 < dialogue.length) {
        speakLine(index + 1);
      } else {
        setIsPlaying(false);
      }
    };

    utterance.onerror = () => {
      setIsPlaying(false);
    };

    synthRef.current.speak(utterance);
  };

  const togglePlay = () => {
    if (isPlaying) {
      if (synthRef.current) synthRef.current.cancel();
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      speakLine(currentLineIndex);
    }
  };

  const handleRestart = () => {
    if (synthRef.current) synthRef.current.cancel();
    setCurrentLineIndex(0);
    setIsPlaying(true);
    speakLine(0);
  };

  const handleSpeedChange = () => {
    const nextSpeed = playbackSpeed === 1.0 ? 1.25 : playbackSpeed === 1.25 ? 1.5 : 1.0;
    setPlaybackSpeed(nextSpeed);
    if (isPlaying) {
      speakLine(currentLineIndex);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "color-mix(in srgb, var(--iv-band) 45%, transparent)",
        backdropFilter: "blur(4px)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(720px, 95vw)",
          maxHeight: "min(620px, 90vh)",
          background: "var(--iv-surface)",
          borderRadius: 14,
          boxShadow: "0 16px 40px color-mix(in srgb, var(--iv-shadow) 18%, transparent)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid var(--iv-line)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--iv-line)",
            background: "var(--iv-bg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "var(--iv-band)",
                color: "var(--iv-on-band)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              V
            </div>
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--iv-body)" }}>
                Cognitive Audio Overview
              </div>
              <div style={{ fontSize: 11.5, color: "var(--iv-muted)" }}>
                Dual-Persona Synthesis · {note?.title || "Untitled Note"}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (synthRef.current) synthRef.current.cancel();
              onClose();
            }}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 6,
              borderRadius: 6,
              color: "var(--iv-muted)",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Dialogue Scroll View */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
            background: "var(--iv-bg)",
          }}
        >
          {dialogue.map((line, idx) => {
            const isActive = currentLineIndex === idx;
            const isA = line.speaker === "Analyst A";

            return (
              <div
                key={idx}
                onClick={() => {
                  setCurrentLineIndex(idx);
                  if (isPlaying) speakLine(idx);
                }}
                style={{
                  padding: "12px 16px",
                  borderRadius: 10,
                  background: isActive ? "var(--iv-surface)" : "var(--iv-raised)",
                  border: isActive ? "1.5px solid var(--iv-head)" : "1px solid var(--iv-line)",
                  boxShadow: isActive ? "0 4px 12px color-mix(in srgb, var(--iv-shadow) 6%, transparent)" : "none",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: isA ? "var(--iv-accent)" : "var(--iv-accent)" }}>
                    {line.speaker} ({line.role})
                  </span>
                  {isActive && isPlaying && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: "var(--iv-body)", display: "flex", alignItems: "center", gap: 4 }}>
                      <Volume2 size={12} /> Speaking
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13.5, color: "var(--iv-body)", lineHeight: 1.55 }}>
                  {line.text}
                </div>
              </div>
            );
          })}
        </div>

        {/* Audio Player Control Bar */}
        <div
          style={{
            padding: "14px 20px",
            borderTop: "1px solid var(--iv-line)",
            background: "var(--iv-surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={togglePlay}
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                border: "none",
                background: "var(--iv-btn)",
                color: "var(--iv-btn-fg)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} style={{ marginLeft: 2 }} />}
            </button>

            <button
              type="button"
              onClick={handleRestart}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid var(--iv-line)",
                background: "var(--iv-bg)",
                color: "var(--iv-body)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              <RotateCcw size={13} /> Restart
            </button>

            <button
              type="button"
              onClick={handleSpeedChange}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid var(--iv-line)",
                background: "var(--iv-bg)",
                color: "var(--iv-body)",
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              {playbackSpeed}x Speed
            </button>
          </div>

          <div style={{ fontSize: 12, color: "var(--iv-muted)" }}>
            Part {currentLineIndex + 1} of {dialogue.length}
          </div>
        </div>
      </div>
    </div>
  );
}
